import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"
import { isValidElement } from "react"

import { cn } from "@/lib/utils"

export type MutedOutlineTone = "neutral" | "primary" | "destructive" | "brand"

const mutedOutlineBaseClass = "border-border/60 bg-transparent text-[#999] shadow-none dark:bg-transparent dark:text-[#999]"
const mutedOutlineToneClasses: Record<MutedOutlineTone, string> = {
  neutral: "not-disabled:not-aria-disabled:hover:border-neutral-500 not-disabled:not-aria-disabled:hover:bg-[#efefef] not-disabled:not-aria-disabled:hover:text-neutral-700 not-disabled:not-aria-disabled:focus-visible:border-neutral-500 not-disabled:not-aria-disabled:focus-visible:bg-[#efefef] not-disabled:not-aria-disabled:focus-visible:text-neutral-700 dark:not-disabled:not-aria-disabled:hover:border-neutral-600 dark:not-disabled:not-aria-disabled:hover:bg-neutral-950/40 dark:not-disabled:not-aria-disabled:hover:text-neutral-300 dark:not-disabled:not-aria-disabled:focus-visible:border-neutral-600 dark:not-disabled:not-aria-disabled:focus-visible:bg-neutral-950/40 dark:not-disabled:not-aria-disabled:focus-visible:text-neutral-300",
  primary: "not-disabled:not-aria-disabled:hover:border-blue-300 not-disabled:not-aria-disabled:hover:bg-blue-50 not-disabled:not-aria-disabled:hover:text-blue-600 not-disabled:not-aria-disabled:focus-visible:border-blue-300 not-disabled:not-aria-disabled:focus-visible:bg-blue-50 not-disabled:not-aria-disabled:focus-visible:text-blue-600 dark:not-disabled:not-aria-disabled:hover:border-blue-800 dark:not-disabled:not-aria-disabled:hover:bg-blue-950/40 dark:not-disabled:not-aria-disabled:hover:text-blue-400 dark:not-disabled:not-aria-disabled:focus-visible:border-blue-800 dark:not-disabled:not-aria-disabled:focus-visible:bg-blue-950/40 dark:not-disabled:not-aria-disabled:focus-visible:text-blue-400",
  destructive: "not-disabled:not-aria-disabled:hover:border-rose-300 not-disabled:not-aria-disabled:hover:bg-rose-50 not-disabled:not-aria-disabled:hover:text-rose-600 not-disabled:not-aria-disabled:focus-visible:border-rose-300 not-disabled:not-aria-disabled:focus-visible:bg-rose-50 not-disabled:not-aria-disabled:focus-visible:text-rose-600 dark:not-disabled:not-aria-disabled:hover:border-rose-800 dark:not-disabled:not-aria-disabled:hover:bg-rose-950/40 dark:not-disabled:not-aria-disabled:hover:text-rose-400 dark:not-disabled:not-aria-disabled:focus-visible:border-rose-800 dark:not-disabled:not-aria-disabled:focus-visible:bg-rose-950/40 dark:not-disabled:not-aria-disabled:focus-visible:text-rose-400",
  brand: "not-disabled:not-aria-disabled:hover:border-brand/40 not-disabled:not-aria-disabled:hover:bg-brand-subtle not-disabled:not-aria-disabled:hover:text-brand not-disabled:not-aria-disabled:focus-visible:border-brand/40 not-disabled:not-aria-disabled:focus-visible:bg-brand-subtle not-disabled:not-aria-disabled:focus-visible:text-brand",
}

export function mutedOutlineButtonClass(tone: MutedOutlineTone = "primary") {
  return cn(mutedOutlineBaseClass, mutedOutlineToneClasses[tone])
}

const buttonVariants = cva(
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80",
        destructiveSolid:
          "bg-rose-500 text-white hover:bg-rose-500/80 dark:bg-rose-600 dark:hover:bg-rose-600/80",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        brandOutline:
          "border-brand/40 bg-transparent text-brand hover:border-brand hover:bg-transparent dark:bg-transparent dark:hover:bg-transparent",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
        mutedOutline: mutedOutlineBaseClass,
      },

      tone: { neutral: "", primary: "", destructive: "", brand: "" },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    compoundVariants: [
      { variant: "mutedOutline", tone: "neutral", className: mutedOutlineToneClasses.neutral },
      { variant: "mutedOutline", tone: "primary", className: mutedOutlineToneClasses.primary },
      { variant: "mutedOutline", tone: "destructive", className: mutedOutlineToneClasses.destructive },
      { variant: "mutedOutline", tone: "brand", className: mutedOutlineToneClasses.brand },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      tone: "primary",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  tone = "primary",
  nativeButton,
  render,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {

  const rendersNativeButton = !(isValidElement(render) && render.type !== "button")

  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, tone, className }))}
      nativeButton={nativeButton ?? rendersNativeButton}
      render={render}
      {...props}
    />
  )
}

export { Button, buttonVariants }
